import { uniqBy } from '@/globals';
import numberFormatter from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IEntityFeeAPI {
  entity_id: number;
  entity_fee_id: number;
  attachable_fee_category_id: number;
  attachable_fee_category?: { fee_category: { name: string; payer: string }; fee_category_id: number };
  type: 'FIXED' | 'PERCENT';
  value: number;
  archived: boolean;
}

export default class EntityFee extends Validatable<EntityFee> {
  constructor(
    public entityFeeID: number = undefined,
    public entityID: number = undefined,
    public attachableFeeCategoryID: number = undefined,
    public feeCategoryName: string = '',
    public feeCategoryPayer: string = 'BUYER',
    public feeCategoryID: number = undefined,
    public feeType: 'FIXED' | 'PERCENT' = 'FIXED',
    public value: number = 0,
    public archived: boolean = false,
  ) {
    super();
  }

  toApi(): IEntityFeeAPI {
    return {
      archived: false,
      entity_fee_id: this.entityFeeID,
      entity_id: this.entityID,
      attachable_fee_category_id: this.attachableFeeCategoryID,
      type: this.feeType,
      value: this.value,
    };
  }

  clone() {
    const clonedObject = JSON.parse(JSON.stringify(this));
    return EntityFee.fromObject(clonedObject);
  }

  get valueFormatted() {
    if (this.feeType === 'FIXED') {
      return numberFormatter(this.value, 'currency');
    }
    return numberFormatter(this.value, '0,00%');
  }

  toString() {
    return `${this.feeCategoryName} - ${this.valueFormatted}${this.feeType === 'FIXED' ? ' CPM' : ''}`;
  }

  static validation(fees: EntityFee[]) {
    if (fees?.length) {
      if (fees.filter((x) => x.value < 0).length) {
        return {
          message: i18n.global.t('messages.lineItemFeesBelowMinValue'),
          preEvaluated: true,
        };
      } else if (fees.filter((x) => x.value > 100 && x.feeType === 'PERCENT').length) {
        return {
          message: i18n.global.t('messages.lineItemFeesAboveMaxValue'),
          preEvaluated: true,
        };
      } else if (fees.filter((x) => !x.feeType || !x.feeCategoryID).length) {
        return {
          message: i18n.global.t('messages.lineItemFeesMissingData'),
          preEvaluated: true,
        };
      } else {
        const feeCategories = uniqBy(fees, 'feeCategoryID');
        if (feeCategories.length !== fees.length) {
          return {
            message: i18n.global.t('messages.lineItemFeesDuplicateCategory'),
            preEvaluated: true,
          };
        }
      }
    }
    return null;
  }

  static fromObject(obj: object) {
    return super.fromObject(obj, new EntityFee());
  }

  static fromApi(apiObject: IEntityFeeAPI) {
    return new EntityFee(
      apiObject.entity_fee_id,
      apiObject.entity_id,
      apiObject.attachable_fee_category_id,
      apiObject.attachable_fee_category?.fee_category?.name,
      apiObject.attachable_fee_category?.fee_category?.payer,
      apiObject.attachable_fee_category?.fee_category_id,
      apiObject.type,
      apiObject.value,
      apiObject.archived,
    );
  }
}
